// src/app/components/analytics/series/performance-kpi-strip.tsx
//
// The "Topline Metrics" strip at the top of Performance. Renders a curated
// topline set (AI Index from Brand Monitoring, then the Deep Signal funnel).
// When a brand has NO Deep Signal run, the funnel cards render as designed
// greyed cards with a blurred value; on hover they reveal a "Run Deep Signal"
// lock, and clicking opens the Deep Signal explainer modal (the same "Talk to
// your Upmode team" card shown on the Deep Signal page).
//
// The topline set is a small declared presentation list (a curated header),
// distinct from data-limiting. Funnel cards lock ONLY when Deep Signal is
// entirely absent; a brand that has Deep Signal but not one specific metric
// simply doesn't show that card (no false lock).

import { useEffect, useMemo, useState } from "react";
import { TrendingUp, TrendingDown, BarChart3, Lock, X } from "lucide-react";
import { fetchSeries } from "@/app/services/performance-series/source";
import { useSeriesSourceMode } from "@/app/services/performance-series/use-source-mode";
import { formatValue, chartScaleFor } from "@/app/services/performance-series/registry";
import { latestOverall, previousOverall } from "@/app/services/performance-series/selectors";
import type { NormalizedEnvelope } from "@/app/services/performance-series/types";
import { DeepSignalLockedCard } from "@/app/components/deep-signal/components/locked-state";

// Curated topline set. `deepSignal` marks the funnel metrics that require a Deep
// Signal run (and lock when it is absent). `ghost` is the blurred placeholder
// shown on a locked card so it reads as a real card, not an empty one.
const TOPLINE: { key: string; label: string; deepSignal?: boolean; ghost?: string }[] = [
  { key: "ai_index", label: "AI Index" },
  { key: "funnel.mention", label: "Mention Rate", deepSignal: true, ghost: "64%" },
  { key: "funnel.top5", label: "Best-in-Category (Top 5)", deepSignal: true, ghost: "41%" },
  // Primary recommendation is the reporting endpoint (7/27); #1 choice is
  // retired from client-facing surfaces.
  { key: "funnel.primary_rec", label: "Primary Recommendation", deepSignal: true, ghost: "8%" },
];

// Decorative accent per data-card slot (by position, not tied to a metric key).
const ACCENT = ["#12B76A", "#FF6D66", "#7E5FE0", "#28A9D2"];

type ToplineCard =
  | { kind: "data"; key: string; label: string; value: string; accent: string; trend: { text: string; positive: boolean } | null }
  | { kind: "locked"; key: string; label: string; ghost: string };

function buildTopline(envelope: NormalizedEnvelope): ToplineCard[] {
  const aiLayer = envelope.layers.find((l) => l.key === "ai_index");
  const aiLast = aiLayer ? latestOverall(aiLayer) : null;
  if (!aiLast) return []; // no monitoring at all -> no topline (view shows empty state)

  const hasDeepSignal = envelope.layers.some((l) => l.source === "deep_signal" && l.overall.length > 0);

  const cards: ToplineCard[] = [];
  TOPLINE.forEach((entry, i) => {
    const layer = envelope.layers.find((l) => l.key === entry.key);
    const last = layer ? latestOverall(layer) : null;
    if (layer && last) {
      const prev = previousOverall(layer);
      let trend: { text: string; positive: boolean } | null = null;
      if (prev) {
        const d = (last.v - prev.v) * chartScaleFor(layer.unit);
        trend = { text: `${d >= 0 ? "+" : ""}${d.toFixed(1)} pts`, positive: d >= 0 };
      }
      cards.push({ kind: "data", key: entry.key, label: entry.label, value: formatValue(layer.unit, last.v), accent: ACCENT[i % ACCENT.length], trend });
    } else if (entry.deepSignal && !hasDeepSignal) {
      cards.push({ kind: "locked", key: entry.key, label: entry.label, ghost: entry.ghost ?? "—" });
    }
    // else: Deep Signal exists but this metric is not measured -> skip, no false lock.
  });
  return cards;
}

export function PerformanceKpiStrip({ brandName }: { brandName?: string }) {
  const [envelope, setEnvelope] = useState<NormalizedEnvelope | null>(null);
  const [lockOpen, setLockOpen] = useState(false);
  const sourceMode = useSeriesSourceMode();

  useEffect(() => {
    let cancelled = false;
    fetchSeries(brandName || "")
      .then((res) => { if (!cancelled) setEnvelope(res.envelope); })
      .catch(() => { if (!cancelled) setEnvelope(null); });
    return () => { cancelled = true; };
  }, [brandName]);

  useEffect(() => {
    if (!lockOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setLockOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lockOpen]);

  const cards = useMemo(() => (envelope ? buildTopline(envelope) : []), [envelope]);
  if (cards.length === 0) return null;

  return (
    <div className="border-b" style={{ padding: "18px clamp(16px, 5vw, 56px)", borderColor: "var(--border)" }}>
      {/* Section header (same weight as the module's section headings). */}
      <div style={{ marginBottom: 14 }}>
        <div className="flex items-center gap-3 mb-1">
          <BarChart3 className="w-5 h-5 flex-shrink-0" style={{ color: "var(--alpha-teal)" }} />
          <h3 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)" }}>
            Topline Metrics
          </h3>
        </div>
        <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)", paddingLeft: 32 }}>
          Current headline numbers, with the change since the last measurement.
        </p>
      </div>

      {sourceMode === "fixture" && (
        <div
          className="flex items-center gap-2"
          style={{ alignSelf: "flex-start", width: "fit-content", background: "var(--muted-30, var(--muted))", border: "1px dashed var(--border)", borderRadius: 9999, padding: "3px 12px", marginBottom: 12 }}
        >
          <span style={{ width: 7, height: 7, borderRadius: 9999, background: "var(--infrared-coral)" }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--muted-foreground)" }}>
            Synthetic data (preview) · not live client measurement
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 xl:grid-cols-4" style={{ gap: 14 }}>
        {cards.map((card) =>
          card.kind === "data" ? (
            <div
              key={card.key}
              style={{ position: "relative", overflow: "hidden", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: 18 }}
            >
              <span style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: card.accent }} />
              <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--muted-foreground)", marginBottom: 10 }}>
                {card.label}
              </div>
              <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: "-.02em", color: "var(--foreground)", fontVariantNumeric: "tabular-nums", lineHeight: 1.1, marginBottom: 6 }}>
                {card.value}
              </div>
              {card.trend ? (
                <div className="flex items-center" style={{ gap: 4 }}>
                  {card.trend.positive
                    ? <TrendingUp style={{ width: 13, height: 13, color: "#04938F" }} />
                    : <TrendingDown style={{ width: 13, height: 13, color: "var(--infrared-coral)" }} />}
                  <span style={{ fontSize: 13, color: card.trend.positive ? "#04938F" : "var(--infrared-coral)" }}>{card.trend.text}</span>
                </div>
              ) : (
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--signal-blue)", background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 9999, padding: "2px 10px" }}>
                  Baseline
                </span>
              )}
            </div>
          ) : (
            // Locked funnel card: a real-looking card with a blurred value; the
            // lock + CTA only appear on hover, and clicking opens the modal.
            <button
              key={card.key}
              type="button"
              onClick={() => setLockOpen(true)}
              className="group"
              title="Deep Signal hasn't run for this brand yet"
              style={{ position: "relative", overflow: "hidden", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: 18, cursor: "pointer", textAlign: "left" }}
            >
              <span style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "var(--border)" }} />
              <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--muted-foreground)", marginBottom: 10 }}>
                {card.label}
              </div>
              <div style={{ position: "relative", height: 38 }}>
                {/* Muted grey placeholder value (no blur — reads as locked, not broken). */}
                <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: "-.02em", color: "var(--muted-foreground)", fontVariantNumeric: "tabular-nums", lineHeight: 1.1, opacity: 0.45, userSelect: "none" }}>
                  {card.ghost}
                </div>
                <div
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", gap: 7, background: "var(--card)", color: "var(--spectral-violet)" }}
                >
                  <Lock style={{ width: 15, height: 15 }} />
                  <span style={{ fontSize: 14, fontWeight: 600 }}>Run Deep Signal</span>
                </div>
              </div>
            </button>
          ),
        )}
      </div>

      {/* Deep Signal explainer modal (reused card). */}
      {lockOpen && (
        <div
          onClick={() => setLockOpen(false)}
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 60, background: "rgba(16,18,22,0.45)", padding: 16 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", width: "100%", maxWidth: 480 }}>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setLockOpen(false)}
              className="text-muted-foreground"
              style={{ position: "absolute", top: 10, right: 10, width: 28, height: 28, borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", zIndex: 1 }}
            >
              <X style={{ width: 16, height: 16 }} />
            </button>
            <DeepSignalLockedCard />
          </div>
        </div>
      )}
    </div>
  );
}
